/**
 * Create Snapshot Modal Component
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { vmApi } from '../../services/vmApi';
import { VMSnapshotCreate } from '../../types/vm';
import { useProjects } from '../../hooks/useProjects';
import { useSearchParams } from 'react-router-dom';
import { Project } from '../../types';

interface CreateSnapshotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialVMId?: string;
}

const CreateSnapshotModal: React.FC<CreateSnapshotModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialVMId,
}) => {
  const [searchParams] = useSearchParams();
  const projectFromUrl = searchParams.get('project');
  const queryClient = useQueryClient();
  const { projects } = useProjects();
  const [selectedProjectId, setSelectedProjectId] = useState<string>(projectFromUrl || '');
  const [vmId, setVMId] = useState<string>(initialVMId || '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Use selected project or URL project
  const projectId = selectedProjectId || undefined;

  // Fetch VMs for selection
  const { data: vmsData } = useQuery({
    queryKey: ['vms', projectId],
    queryFn: () => vmApi.listVMs({ project_id: projectId }),
    enabled: !!projectId && !initialVMId,
  });

  const vms = vmsData?.vms || [];

  // Create snapshot mutation
  const createMutation = useMutation({
    mutationFn: (data: VMSnapshotCreate) => vmApi.createSnapshot(vmId, data),
    onSuccess: () => {
      toast.success('Snapshot creation initiated');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create snapshot');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!projectId) {
      toast.error('Please select a project');
      return;
    }

    if (!vmId) {
      toast.error('Please select a VM');
      return;
    }

    if (!name.trim()) {
      toast.error('Please enter a snapshot name');
      return;
    }

    createMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-gray-900 border border-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-gray-900 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Create Snapshot</h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!initialVMId && (
                <>
                  {/* Project Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Project *
                    </label>
                    <select
                      value={selectedProjectId}
                      onChange={(e) => {
                        setSelectedProjectId(e.target.value);
                        setVMId(''); // Reset VM selection when project changes
                      }}
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    >
                      <option value="">Select a project</option>
                      {projects.map((project: Project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-400">
                      Select the project containing the VM you want to snapshot
                    </p>
                  </div>

                  {/* VM Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Virtual Machine *
                    </label>
                    {!projectId ? (
                      <div className="w-full px-3 py-2 border border-gray-700 rounded-md bg-gray-800">
                        <p className="text-sm text-gray-400">
                          Please select a project first
                        </p>
                      </div>
                    ) : (
                      <>
                        <select
                          value={vmId}
                          onChange={(e) => setVMId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          required
                          disabled={!projectId}
                        >
                          <option value="">Select a VM</option>
                          {vms
                            .filter(vm => vm.status === 'running' || vm.status === 'stopped')
                            .map((vm) => (
                              <option key={vm.id} value={vm.id}>
                                {vm.name} ({vm.status})
                              </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-400">
                          Only running or stopped VMs can be snapshotted
                        </p>
                      </>
                    )}
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Snapshot Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., backup-2024-01-15"
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                  maxLength={255}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description for this snapshot..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  maxLength={500}
                />
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-500 text-base font-medium text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Snapshot'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-600 shadow-sm px-4 py-2 bg-gray-800 text-base font-medium text-gray-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSnapshotModal;

